/**
 * Eintrag der Einkaufsliste: großer Tippbereich zum Abhaken, Menge, Herkunft
 * und Packungsvorschlag. Über den Stift lässt sich der Eintrag bearbeiten.
 */
import { SHOPPING_CATEGORIES, parseQuantity } from '@cookbook/domain';
import { useState } from 'react';
import type { ReactNode } from 'react';
import type { ShoppingItem } from '../../api/types.ts';
import { itemAmount, packageHint } from '../../lib/shoppingFormat.ts';
import { texts } from '../../texts/de.ts';
import { AppIcon } from '../AppIcon.tsx';

/** Änderungen, die der Editor liefert. */
export interface ItemChanges {
  name: string;
  amount: number | null;
  unit: string;
  note: string | null;
  category: string;
}

/** Eigenschaften eines Eintrags. */
export interface ShoppingItemRowProps {
  readonly item: ShoppingItem;
  readonly onToggle: () => void;
  readonly onChange: (changes: ItemChanges) => void;
  readonly onRemove: () => void;
  /** Griff zum Verschieben, links vor dem Eintrag. */
  readonly handle?: ReactNode;
  /** Angaben für das Ziehen und Ablegen (data-Attribute). */
  readonly sortProps?: Readonly<Record<string, string>>;
  /** Wird gerade gezogen. */
  readonly dragging?: boolean;
}

/** Bearbeitung eines Eintrags. */
function ItemEditor({
  item,
  onSave,
  onRemove,
  onClose,
}: {
  readonly item: ShoppingItem;
  readonly onSave: (changes: ItemChanges) => void;
  readonly onRemove: () => void;
  readonly onClose: () => void;
}) {
  const [name, setName] = useState(item.name);
  const [amount, setAmount] = useState(item.amount === null ? '' : String(item.amount).replace('.', ','));
  const [unit, setUnit] = useState(item.unit);
  const [note, setNote] = useState(item.note ?? '');
  const [category, setCategory] = useState(item.category);

  return (
    <form
      className="mt-2 grid grid-cols-2 gap-2 rounded-xl bg-surface-sunken p-3 text-sm sm:grid-cols-4"
      onSubmit={(event) => {
        event.preventDefault();
        const quantity = parseQuantity(amount);
        onSave({
          name: name.trim() === '' ? item.name : name.trim(),
          amount: quantity === null ? null : (quantity.max ?? quantity.value),
          unit: unit.trim(),
          note: note.trim() === '' ? null : note.trim(),
          category,
        });
        onClose();
      }}
    >
      <label className="col-span-2">
        <span className="block text-xs text-content-muted">{texts.shopping.name}</span>
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          className="w-full rounded-lg border border-border-subtle bg-surface px-2 py-1.5"
        />
      </label>
      <label>
        <span className="block text-xs text-content-muted">{texts.shopping.amount}</span>
        <input
          value={amount}
          inputMode="decimal"
          onChange={(event) => setAmount(event.target.value)}
          className="w-full rounded-lg border border-border-subtle bg-surface px-2 py-1.5"
        />
      </label>
      <label>
        <span className="block text-xs text-content-muted">{texts.shopping.unit}</span>
        <input
          value={unit}
          onChange={(event) => setUnit(event.target.value)}
          className="w-full rounded-lg border border-border-subtle bg-surface px-2 py-1.5"
        />
      </label>
      <label className="col-span-2">
        <span className="block text-xs text-content-muted">{texts.shopping.note}</span>
        <input
          value={note}
          onChange={(event) => setNote(event.target.value)}
          className="w-full rounded-lg border border-border-subtle bg-surface px-2 py-1.5"
        />
      </label>
      <label className="col-span-2">
        <span className="block text-xs text-content-muted">{texts.shopping.category}</span>
        <select
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          className="w-full rounded-lg border border-border-subtle bg-surface px-2 py-1.5"
        >
          {SHOPPING_CATEGORIES.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.label}
            </option>
          ))}
        </select>
      </label>
      <div className="col-span-2 flex flex-wrap gap-2 sm:col-span-4">
        <button type="submit" className="rounded-lg bg-accent px-3 py-1.5 font-semibold text-on-accent">
          {texts.shopping.save}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-border-subtle px-3 py-1.5"
        >
          {texts.common.cancel}
        </button>
        <button
          type="button"
          onClick={() => {
            onRemove();
            onClose();
          }}
          className="ml-auto rounded-lg border border-border-subtle px-3 py-1.5 text-content-muted"
        >
          {texts.shopping.remove}
        </button>
      </div>
    </form>
  );
}

/** Eintrag der Einkaufsliste. */
export function ShoppingItemRow({
  item,
  onToggle,
  onChange,
  onRemove,
  handle,
  sortProps,
  dragging = false,
}: ShoppingItemRowProps) {
  const [editing, setEditing] = useState(false);
  const amount = itemAmount(item);
  const pack = item.checked ? null : packageHint(item);
  const details = [
    item.note,
    item.sources.length === 0
      ? null
      : `${texts.shopping.from} ${item.sources.map((source) => source.title).join(', ')}`,
  ].filter((part): part is string => part !== null && part !== '');

  return (
    <li
      {...sortProps}
      className={`py-1 print:break-inside-avoid ${dragging ? 'relative z-10 rounded-xl bg-surface-raised shadow-lg ring-2 ring-accent' : ''}`}
    >
      <div className="flex items-center gap-1">
        {handle}
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={item.checked}
          className={`flex min-h-12 flex-1 items-center gap-3 rounded-xl px-2 py-1.5 text-left ${
            item.checked ? 'text-content-muted' : 'hover:bg-surface-sunken'
          }`}
        >
          <span
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 print:h-5 print:w-5 print:rounded-md print:border-content ${
              item.checked ? 'border-accent bg-accent text-on-accent' : 'border-border-subtle'
            }`}
          >
            {item.checked ? <AppIcon name="check" size={16} /> : null}
          </span>
          {item.product?.thumbUrl ? (
            <img src={item.product.thumbUrl} alt="" className="h-9 w-9 shrink-0 rounded-lg object-cover" />
          ) : null}
          <span className="min-w-0 flex-1">
            <span className={`block ${item.checked ? 'line-through' : ''}`}>
              {amount === '' ? null : <strong className="mr-1 tabular-nums">{amount}</strong>}
              {item.name}
            </span>
            {details.length === 0 && pack === null ? null : (
              <span className="block truncate text-xs text-content-muted">
                {[...details, pack?.text].filter((part) => part !== undefined).join(' · ')}
              </span>
            )}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setEditing((value) => !value)}
          aria-label={`${texts.shopping.edit}: ${item.name}`}
          aria-expanded={editing}
          className="rounded-lg p-2 text-content-muted hover:bg-surface-sunken print:hidden"
        >
          <AppIcon name="sliders" size={18} />
        </button>
      </div>
      {editing ? (
        <ItemEditor item={item} onSave={onChange} onRemove={onRemove} onClose={() => setEditing(false)} />
      ) : null}
    </li>
  );
}
